/**
 * Best-effort detection of the runtime platform. Sent to the backend alongside
 * the app version so it can tell iOS from Android (e.g. to tailor install
 * instructions or update prompts).
 *
 * @returns {'ios'|'android'|'desktop'|'unknown'}
 */
export function detectPlatform() {
  try {
    const ua = navigator.userAgent || '';

    // iPadOS 13+ reports itself as macOS — a touch-capable "Mac" is really an iPad.
    const isIpadOs = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
    if (isIpadOs || /iphone|ipad|ipod/i.test(ua)) return 'ios';
    if (/android/i.test(ua)) return 'android';
    if (/windows|macintosh|mac os|linux|cros/i.test(ua)) return 'desktop';
    return 'unknown';
  } catch {
    return 'unknown';
  }
}

/**
 * Best-effort check for "we are inside a native app's WebView", not a browser.
 *
 * Those containers serve *this very bundle* from the app package, so the page
 * origin is a loopback one — `capacitor://localhost` on iOS, `https://localhost`
 * on Android, `ionic://localhost` with the older Cordova WebView — and every API
 * call is cross-origin. That is what the backend's `CorsSubscriber` matches by
 * shape, and why `API_BASE_URL` must stay an absolute host in a bundle shipped
 * inside the app: a same-origin (empty) value would resolve against the WebView's
 * own origin and reach no API at all.
 *
 * Detection order matters: the bridge object is authoritative (Android's WebView
 * runs on plain `https://`, indistinguishable from a browser by URL alone), and
 * the scheme check is only the fallback for wrappers that inject no bridge.
 *
 * @returns {boolean}
 */
export function isNativeWebView() {
  try {
    const capacitor = window.Capacitor;
    if (capacitor) {
      // Present in web builds too when @capacitor/core is bundled, so ask it.
      return typeof capacitor.isNativePlatform === 'function'
        ? Boolean(capacitor.isNativePlatform())
        : Boolean(capacitor.isNative);
    }
    if (window.cordova) return true;

    // A browser — installed PWA included — is always served over http(s).
    return !/^https?:$/.test(window.location.protocol);
  } catch {
    return false;
  }
}
